import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { Avatar, Badge, Switch, Channel } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

interface ConversationRow {
  conversation_id: string;
  channel_id: string;
  channel_type: 'whatsapp' | 'instagram' | 'facebook';
  customer_id: string;
  customer_name: string | null;
  customer_phone: string | null;
  ai_status: 'active' | 'paused_auto' | 'paused_manual' | 'disabled';
  last_inbound_at: string | null;
  unread_count: number;
  last_text: string | null;
  tags: string[];
}

const AI_KIND: Record<ConversationRow['ai_status'], 'ok' | 'warn' | 'accent' | 'muted'> = {
  active: 'ok', paused_auto: 'warn', paused_manual: 'accent', disabled: 'muted',
};

// WhatsApp is escalation-only via wa.me — we never receive WA messages here.
const CHANNELS = ['all', 'instagram', 'facebook'] as const;

export function Inbox() {
  const api = useApi();
  const { t } = useLang();
  const [channel, setChannel] = useState<typeof CHANNELS[number]>('all');
  const [aiStatus, setAiStatus] = useState<string>('');
  const [hasUnread, setHasUnread] = useState(false);
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['inbox', channel, aiStatus, hasUnread],
    queryFn: () => {
      const params = new URLSearchParams();
      if (channel !== 'all') params.set('channel_type', channel);
      if (aiStatus) params.set('ai_status', aiStatus);
      if (hasUnread) params.set('has_unread', 'true');
      return api.get<{ items: ConversationRow[]; cursor: string | null }>(`/conversations?${params}`);
    },
    refetchInterval: 30_000,
  });

  const rows = useMemo(() => {
    const all = data?.items ?? [];
    if (!q.trim()) return all;
    const needle = q.trim().toLowerCase();
    return all.filter((c) =>
      (c.customer_name ?? '').toLowerCase().includes(needle) ||
      (c.customer_phone ?? '').toLowerCase().includes(needle) ||
      (c.last_text ?? '').toLowerCase().includes(needle),
    );
  }, [data?.items, q]);

  return (
    <div className="inbox__list" style={{ height: 'calc(100vh - var(--topbar-h))', borderInlineEnd: 0, width: '100%' }}>
      <div className="inbox__listHead">
        <div className="between" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{t('page.inbox.title')}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              <span className="mono">{rows.length}</span> {t('page.inbox.count')}
            </div>
          </div>
          {data && data.items.some((r) => r.unread_count > 0) && (
            <Badge kind="accent" plain>{t('page.inbox.unreadBadge')}</Badge>
          )}
        </div>

        <div className="inbox__search" style={{ marginBottom: 10 }}>
          <Icon name="search" />
          <input placeholder={t('page.inbox.search.ph')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        <div className="seg" style={{ width: '100%' }}>
          {CHANNELS.map((ch) => (
            <button key={ch} className={channel === ch ? 'on' : ''} onClick={() => setChannel(ch)} style={{ flex: 1, padding: '7px 8px', fontSize: 12 }}>
              {ch === 'all' ? t('common.all') : t(`channel.${ch}`)}
            </button>
          ))}
        </div>

        <div className="flex aic gap12" style={{ marginTop: 12, flexWrap: 'wrap' }}>
          <label className="flex aic gap8" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
            <Switch on={hasUnread} onClick={() => setHasUnread((v) => !v)} />
            {t('page.inbox.unreadOnly')}
          </label>
          <select
            className="selectbox"
            style={{ padding: '7px 10px', fontSize: 12.5, width: 'auto', flex: 'none' }}
            value={aiStatus}
            onChange={(e) => setAiStatus(e.target.value)}
          >
            <option value="">{t('page.inbox.aistatus.all')}</option>
            <option value="active">{t('ai.active')}</option>
            <option value="paused_auto">{t('ai.paused_auto')}</option>
            <option value="paused_manual">{t('ai.paused_manual')}</option>
            <option value="disabled">{t('ai.disabled')}</option>
          </select>
        </div>
      </div>

      <div className="inbox__rows">
        {isLoading ? (
          <div className="muted" style={{ padding: 24, fontSize: 13 }}>{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '40px 22px', textAlign: 'center' }}>
            <div className="empty__icon" style={{ margin: '0 auto 14px' }}><Icon name="inbox" /></div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{t('page.inbox.empty.h')}</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>{t('page.inbox.empty.p')}</div>
          </div>
        ) : (
          rows.map((c) => (
            <Link key={c.conversation_id} to={`/conversations/${c.conversation_id}`} className="convrow">
              <Avatar initial={((c.customer_name?.[0] ?? c.customer_phone?.[0] ?? '?')).toUpperCase()} size={40} grad={false} />
              <div className="convrow__main">
                <div className="convrow__top">
                  <span className="convrow__name">{c.customer_name ?? c.customer_phone ?? t('common.unknown')}</span>
                  <span className="convrow__time">{timeAgo(c.last_inbound_at, t)}</span>
                </div>
                <div className="convrow__msg">{c.last_text ?? '—'}</div>
                <div className="convrow__meta">
                  <Channel ch={c.channel_type} />
                  <Badge kind={AI_KIND[c.ai_status]} plain>{t(`ai.${c.ai_status}`)}</Badge>
                </div>
              </div>
              <div className="convrow__right">
                {c.unread_count > 0 && (
                  <span style={{
                    background: 'var(--accent)', color: '#0c0822', minWidth: 22, height: 22,
                    padding: '0 7px', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--f-mono)',
                  }}>{c.unread_count}</span>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

function timeAgo(iso: string | null, t: (k: string) => string): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return t('time.now');
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}${t('time.min')}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${t('time.hour')}`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}${t('time.day')}`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
